import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { trpc } from '@org/clients/school';
import { cn } from '../../../../utils';
import { renderInlineMarkdown } from '../../../custom/markdown';
import { Mic, Phone, PhoneOff, Loader2, MessageCircle, Sparkles, History, ChevronDown, ChevronUp } from 'lucide-react';
import { AudioSettingsPopover, useAudioSettings } from '../../../custom/audio-settings';
import { Button } from '../../../core/button';
import { Badge } from '../../../core/badge';
import { Alert, AlertDescription } from '../../../core/alert';
import { Tooltip, TooltipTrigger, TooltipContent } from '../../../core/tooltip';
import { ContentDescription } from '../../components/shared/content-description';
import { ExerciseFeedbackList } from '../../components/shared/exercise-feedback-list';
import { ElevenLabsConversationUI } from '../../components/contents/elevenlabs-conversation-ui';
import { SessionResult } from '../../components/contents/session-result';
import { SessionHistoryList } from '../../components/contents/session-history-list';
import { useOpenAIRealtime } from '../../components/contents/use-openai-realtime';
import { formatTime } from '../../components/contents/conversational-exercise-types';
import type {
  ConversationalExerciseProps,
  ConversationProvider,
  ConversationState,
  ConversationTurn,
  CompletionResult,
  SessionSummary,
  SessionWithTurns,
} from '../../components/contents/conversational-exercise-types';

export type { ConversationalExerciseProps };

export function ConversationalExercise({
  content,
  feedback,
  variant = 'card',
  isReadOnly = false,
  viewerStudentId,
}: ConversationalExerciseProps) {
  const [conversationState, setConversationState] = useState<ConversationState>('idle');
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [completionResult, setCompletionResult] = useState<CompletionResult | null>(null);
  const [selectedHistorySessionId, setSelectedHistorySessionId] = useState<string | null>(null);
  const [showHistoryList, setShowHistoryList] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Provider-specific state
  const [provider, setProvider] = useState<ConversationProvider>('openai');
  const [ephemeralKey, setEphemeralKey] = useState<string | null>(null);
  const [elevenlabsContext, setElevenlabsContext] = useState<string | undefined>(undefined);

  // Audio device selection
  const { selectedMicId, setSelectedMicId, selectedSpeakerId, setSelectedSpeakerId, getMicConstraints, setSpeakerOnElement } = useAudioSettings();

  const turnsContainerRef = useRef<HTMLDivElement>(null);

  // OpenAI Realtime hook (WebRTC, push-to-talk, timer, idle detection)
  const { t } = useTranslation('p2e');
  // Every way a call can end (End button, timer, inactivity, the AI, lost
  // connection, leaving the page) reports back through onEnding/onEnded.
  const openai = useOpenAIRealtime({
    maxDurationSec: content.maxDurationSec ?? 300,
    getMicConstraints,
    setSpeakerOnElement,
    onEnding: () => setConversationState('ending'),
    onEnded: ({ result, error: endError }) => {
      if (result) {
        setCompletionResult(result);
      } else if (endError) {
        setError(t('zine.speak.endedNoResult'));
      }
      setConversationState('completed');
    },
  });

  // tRPC queries for past sessions
  const { data: sessionsData, isLoading: isLoadingSessions } = trpc.conversation.getSessions.useQuery(
    {
      exerciseId: content.exerciseId,
      limit: 10,
      ...(viewerStudentId ? { studentId: viewerStudentId } : {}),
    },
    { staleTime: 1000 * 60 * 5 }
  );

  const { data: selectedSessionData, isLoading: isLoadingSelectedSession } = trpc.conversation.getSession.useQuery(
    { sessionId: selectedHistorySessionId! },
    { enabled: !!selectedHistorySessionId, staleTime: 1000 * 60 * 5 }
  );

  const startSessionMutation = trpc.conversation.startSession.useMutation();

  const previousSessions = useMemo<SessionSummary[]>(() => {
    if (!sessionsData) return [];
    return (sessionsData as SessionSummary[])
      .filter((s) => s.status === 'completed')
      .sort((a, b) => new Date(b.startedAt ?? 0).getTime() - new Date(a.startedAt ?? 0).getTime());
  }, [sessionsData]);

  // Auto-scroll to bottom when new turns are added
  useEffect(() => {
    if (turnsContainerRef.current) {
      turnsContainerRef.current.scrollTop = turnsContainerRef.current.scrollHeight;
    }
  }, [openai.turns]);

  // Leaving the page mid-call: finish the session on the server instead of leaving it hanging.
  useEffect(() => {
    return () => {
      if (openai.isActive()) {
        void openai.endConversation('unmount').catch(() => undefined);
      } else {
        openai.cleanup();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEndConversation = useCallback(() => {
    // State transitions happen in onEnding/onEnded (shared with every other end path).
    void openai.endConversation('user').catch(() => undefined);
  }, [openai]);

  const handleStartConversation = async () => {
    try {
      setConversationState('connecting');
      setError(null);
      openai.clearError();

      const session = await startSessionMutation.mutateAsync({
        exerciseId: content.exerciseId,
      });

      if (!session.sessionId || !session.ephemeralKey) {
        throw new Error('Invalid session response from server');
      }

      setSessionId(session.sessionId);
      setProvider((session.provider as ConversationProvider) || 'openai');
      setEphemeralKey(session.ephemeralKey);

      // For ElevenLabs, store the context and let ElevenLabsConversationUI handle connection
      if (session.provider === 'elevenlabs') {
        setElevenlabsContext(session.elevenlabsContext);
        setConversationState('active');
        return;
      }

      // For OpenAI: connect via WebRTC
      await openai.connect({
        sessionId: session.sessionId,
        ephemeralKey: session.ephemeralKey,
      });

      setConversationState('active');
    } catch (err) {
      console.error('Failed to start conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to start conversation');
      setConversationState('idle');
      openai.cleanup();
    }
  };

  const handleStartNewConversation = () => {
    setConversationState('idle');
    setSessionId(null);
    setCompletionResult(null);
    setSelectedHistorySessionId(null);
    setError(null);
    openai.clearError();
    setProvider('openai');
    setEphemeralKey(null);
    setElevenlabsContext(undefined);
  };

  const handleViewHistorySession = (session: SessionSummary) => {
    if (session.sessionId) {
      setSelectedHistorySessionId(session.sessionId);
      setConversationState('viewing_history');
      setShowHistoryList(false);
    }
  };

  const handleBackFromHistory = () => {
    setSelectedHistorySessionId(null);
    setConversationState('idle');
  };

  const displayError = error || openai.error;

  return (
    <div
      className={cn(
        'space-y-6',
        variant === 'card' && 'rounded-[24px] border border-rose-100 bg-white/90 p-6 shadow-sm backdrop-blur'
      )}
    >
      {/* Header with title and audio settings */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          {content.title && (
            <h3 className="text-xl font-semibold text-gray-900">
              {renderInlineMarkdown(content.title, 'conversational-exercise-title')}
            </h3>
          )}
        </div>
        {!isReadOnly && (
          <AudioSettingsPopover
            selectedMicId={selectedMicId}
            onMicSelect={setSelectedMicId}
            selectedSpeakerId={selectedSpeakerId}
            onSpeakerSelect={setSelectedSpeakerId}
            showSpeakerSelection={true}
            disabled={conversationState === 'active' || conversationState === 'ending'}
          />
        )}
      </div>
      <ContentDescription text={content.description} markdown />

      {content.theme && (
        <Badge variant="secondary" className="gap-2 px-4 py-2 bg-gradient-to-r from-rose-50 to-pink-50 border-rose-100 text-gray-700">
          <MessageCircle className="h-4 w-4 text-[#cf2e2e]" />
          <span><span className="font-semibold text-[#cf2e2e]">Theme:</span> {content.theme}</span>
        </Badge>
      )}

      {displayError && (
        <Alert variant="destructive" className="rounded-2xl">
          <AlertDescription>{displayError}</AlertDescription>
        </Alert>
      )}

      {/* Idle state — start button + history */}
      {conversationState === 'idle' && !isReadOnly && (
        <IdleView
          onStart={handleStartConversation}
          isPending={startSessionMutation.isPending}
          maxDurationSec={content.maxDurationSec ?? 300}
          previousSessions={previousSessions}
          showHistoryList={showHistoryList}
          onToggleHistory={() => setShowHistoryList(!showHistoryList)}
          onViewSession={handleViewHistorySession}
        />
      )}

      {/* Connecting state (OpenAI only) */}
      {conversationState === 'connecting' && provider !== 'elevenlabs' && (
        <div className="flex flex-col items-center space-y-6 py-12">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#cf2e2e] to-[#f78da7] opacity-30 blur-xl animate-pulse" />
            <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7]">
              <Loader2 className="h-10 w-10 animate-spin text-white" />
            </div>
          </div>
          <div className="text-center">
            <p className="text-sm font-semibold text-gray-700">Connecting...</p>
            <p className="text-xs text-gray-500 mt-1">Setting up your conversation</p>
          </div>
        </div>
      )}

      {/* ElevenLabs Conversation UI */}
      {(conversationState === 'active' || conversationState === 'connecting') &&
        provider === 'elevenlabs' &&
        sessionId &&
        ephemeralKey && (
          <ElevenLabsConversationUI
            content={content}
            session={{ sessionId, ephemeralKey, elevenlabsContext }}
            feedback={feedback}
            variant={variant}
            onEnd={handleEndConversation}
            onTurnSaved={(role, turnContent) => {
              // ElevenLabs manages its own turns; we don't need openai.turns here
              void role;
              void turnContent;
            }}
          />
        )}

      {/* OpenAI Conversation UI — Push-to-talk */}
      {(conversationState === 'active' || conversationState === 'ending') && provider !== 'elevenlabs' && (
        <OpenAIActiveView
          turns={openai.turns}
          isRecording={openai.isRecording}
          currentTranscript={openai.currentTranscript}
          timeRemaining={openai.timeRemaining}
          idleSecondsLeft={openai.idleSecondsLeft}
          quietSeconds={openai.quietSeconds}
          isWrappingUp={openai.isWrappingUp}
          conversationState={conversationState}
          turnsContainerRef={turnsContainerRef}
          onEndConversation={handleEndConversation}
          onToggleRecording={openai.handleToggleRecording}
          onPointerDown={openai.handlePointerDown}
          onPointerUp={openai.handlePointerUp}
        />
      )}

      {/* Completed state */}
      {conversationState === 'completed' && completionResult && (
        <SessionResult
          session={null}
          result={completionResult}
          sessionTurns={openai.turns}
          feedback={feedback}
          onStartNew={handleStartNewConversation}
          isReadOnly={isReadOnly}
        />
      )}

      {/* Ended without a result (evaluation unavailable) — never a dead end */}
      {conversationState === 'completed' && !completionResult && !isReadOnly && (
        <div className="flex justify-center">
          <Button onClick={handleStartNewConversation} variant="outline" className="rounded-full">
            {t('zine.speak.startNew')}
          </Button>
        </div>
      )}

      {/* Viewing history state */}
      {conversationState === 'viewing_history' && selectedHistorySessionId && (
        isLoadingSelectedSession ? (
          <div className="flex flex-col items-center space-y-4 py-8">
            <Loader2 className="h-8 w-8 animate-spin text-rose-300" />
            <p className="text-sm text-gray-500">Loading conversation...</p>
          </div>
        ) : selectedSessionData ? (
          <SessionResult
            session={selectedSessionData as SessionWithTurns}
            result={null}
            sessionTurns={[]}
            feedback={feedback}
            onBackFromHistory={handleBackFromHistory}
            onStartNew={handleStartNewConversation}
            isReadOnly={isReadOnly}
          />
        ) : (
          <div className="text-center text-gray-500 py-8">
            <p>Session not found</p>
            <Button onClick={handleBackFromHistory} variant="link" className="text-[#cf2e2e] mt-2">
              Go back
            </Button>
          </div>
        )
      )}

      {/* Read-only mode with no active conversation */}
      {isReadOnly && conversationState === 'idle' && !selectedHistorySessionId && (
        <ReadOnlyView
          isLoading={isLoadingSessions}
          previousSessions={previousSessions}
          onViewSession={handleViewHistorySession}
        />
      )}

      {/* Show feedback for non-completed states */}
      {feedback && feedback.length > 0 && conversationState !== 'completed' && conversationState !== 'viewing_history' && (
        <ExerciseFeedbackList feedback={feedback} />
      )}
    </div>
  );
}

// --- Sub-components (colocated, not exported) ---

interface IdleViewProps {
  onStart: () => void;
  isPending: boolean;
  maxDurationSec: number;
  previousSessions: SessionSummary[];
  showHistoryList: boolean;
  onToggleHistory: () => void;
  onViewSession: (session: SessionSummary) => void;
}

function IdleView({
  onStart,
  isPending,
  maxDurationSec,
  previousSessions,
  showHistoryList,
  onToggleHistory,
  onViewSession,
}: IdleViewProps) {
  return (
    <div className="flex flex-col items-center space-y-6 py-10">
      <button
        onClick={onStart}
        disabled={isPending}
        className="relative group disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7] opacity-20 blur-xl animate-pulse group-hover:opacity-30" />
        <div className="relative flex h-28 w-28 items-center justify-center rounded-full bg-gradient-to-br from-[#cf2e2e] via-[#f05f7a] to-[#f78da7] shadow-lg transition-all duration-200 group-hover:scale-105 group-hover:shadow-xl group-active:scale-95">
          {isPending ? (
            <Loader2 className="h-12 w-12 text-white animate-spin" />
          ) : (
            <MessageCircle className="h-12 w-12 text-white" />
          )}
        </div>
      </button>

      <div className="text-center space-y-2">
        <p className="text-xs uppercase tracking-[0.2em] text-[#cf2e2e] font-semibold">
          AI Voice Conversation
        </p>
        <p className="text-sm text-gray-600 max-w-xs">
          Practice speaking with an AI tutor using push-to-talk. Click the mic to speak, click again to send.
        </p>
        <div className="flex items-center justify-center gap-2 text-xs text-gray-500">
          <Sparkles className="h-3 w-3" />
          <span>Max duration: {formatTime(maxDurationSec)}</span>
        </div>
      </div>

      <Button
        onClick={onStart}
        disabled={isPending}
        variant="submit"
        size="submit"
        className="group gap-3"
      >
        {isPending ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-white/20 transition group-hover:bg-white/30">
            <Phone className="h-5 w-5" />
          </div>
        )}
        <span>Start Conversation</span>
      </Button>

      {previousSessions.length > 0 && (
        <div className="w-full max-w-md">
          <Button
            onClick={onToggleHistory}
            variant="ghost"
            className="w-full gap-2 text-gray-600 hover:text-[#cf2e2e]"
          >
            <History className="h-4 w-4" />
            <span>View previous attempts ({previousSessions.length})</span>
            {showHistoryList ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </Button>

          {showHistoryList && (
            <div className="mt-2 shadow-sm animate-in fade-in slide-in-from-top-2 duration-200">
              <SessionHistoryList sessions={previousSessions} onSelect={onViewSession} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

interface OpenAIActiveViewProps {
  turns: ConversationTurn[];
  isRecording: boolean;
  currentTranscript: string;
  timeRemaining: number;
  idleSecondsLeft: number | null;
  quietSeconds: number;
  isWrappingUp: boolean;
  conversationState: ConversationState;
  turnsContainerRef: React.RefObject<HTMLDivElement | null>;
  onEndConversation: () => void;
  onToggleRecording: () => void;
  onPointerDown: () => void;
  onPointerUp: () => void;
}

/** After this many quiet seconds (nobody talking, AI idle) the mic label nudges the student. */
const QUIET_HINT_SEC = 10;

function OpenAIActiveView({
  turns,
  isRecording,
  currentTranscript,
  timeRemaining,
  idleSecondsLeft,
  quietSeconds,
  isWrappingUp,
  conversationState,
  turnsContainerRef,
  onEndConversation,
  onToggleRecording,
  onPointerDown,
  onPointerUp,
}: OpenAIActiveViewProps) {
  const { t } = useTranslation('p2e');
  // Nudge only when the line is truly quiet (off-mic: nobody talking; on-mic: no voice
  // reaching the open mic) and no stronger notice (countdown / wrap-up) is on screen.
  const showQuietHint = quietSeconds >= QUIET_HINT_SEC && idleSecondsLeft === null && !isWrappingUp;
  // Keep the pointer on the button while holding, so a finger drifting off the
  // edge does not cut the recording in half.
  const handlePointerDown = (event: React.PointerEvent<HTMLButtonElement>) => {
    try {
      event.currentTarget.setPointerCapture(event.pointerId);
    } catch {
      // ignore: capture is best-effort
    }
    onPointerDown();
  };
  return (
    <div className="flex flex-col min-h-[400px]">
      {/* Conversation transcript */}
      <div
        ref={turnsContainerRef}
        className={cn(
          'flex-1 space-y-3 overflow-y-auto rounded-t-[24px] p-4 sm:p-5 transition-colors duration-300',
          isRecording
            ? 'bg-gradient-to-b from-rose-950/5 to-rose-900/10'
            : 'bg-gradient-to-b from-slate-50 to-white'
        )}
        style={{ maxHeight: '280px', minHeight: '120px' }}
      >
        {turns.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full py-6 text-center">
            <p className="text-sm text-slate-400">
              {isRecording ? 'Listening...' : 'Tap the mic button below to speak'}
            </p>
          </div>
        ) : (
          turns.map((turn, index) => (
            <div
              key={index}
              className={cn(
                'flex animate-in fade-in slide-in-from-bottom-2 duration-300',
                turn.role === 'user' ? 'justify-end' : 'justify-start'
              )}
            >
              <div
                className={cn(
                  'max-w-[85%] rounded-2xl px-4 py-2.5',
                  turn.role === 'user'
                    ? 'bg-slate-800 text-white'
                    : 'bg-white border border-slate-200 text-slate-700 shadow-sm'
                )}
              >
                {turn.pending ? (
                  <p className="animate-pulse text-sm leading-relaxed opacity-70">…</p>
                ) : turn.failed ? (
                  <p className="text-xs italic leading-relaxed opacity-70">{t('zine.speak.transcriptFailed')}</p>
                ) : (
                  <p className="text-sm leading-relaxed">{turn.content}</p>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Inactivity countdown (last minute only) / wrap-up notice */}
      {idleSecondsLeft !== null && !isRecording && (
        <div className="bg-rose-50 px-4 py-2 text-center text-xs font-semibold text-rose-700" role="status">
          {t('zine.speak.idleCountdown', { seconds: idleSecondsLeft })}
        </div>
      )}
      {idleSecondsLeft === null && isWrappingUp && (
        <div className="bg-slate-50 px-4 py-2 text-center text-xs font-semibold text-slate-600" role="status">
          {t('zine.speak.wrappingUp')}
        </div>
      )}

      {/* Push-to-Talk Control Zone */}
      <div className={cn(
        'rounded-b-[24px] px-4 py-4 sm:py-6 transition-all duration-300',
        isRecording
          ? 'bg-gradient-to-t from-rose-600 via-rose-500 to-rose-400'
          : 'bg-gradient-to-t from-slate-100 via-slate-50 to-white border-t border-slate-100'
      )}>
        {/* Timer and End button */}
        <div className="flex items-center justify-between mb-4">
          <div className={cn(
            'flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-colors shrink-0',
            isRecording ? 'bg-white/20 text-white' : 'bg-slate-200/80 text-slate-600'
          )}>
            <div className={cn(
              'w-1.5 h-1.5 rounded-full shrink-0',
              isRecording ? 'bg-white animate-pulse' : 'bg-emerald-500'
            )} />
            <span className="font-mono tracking-wide">{formatTime(timeRemaining)}</span>
          </div>

          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={onEndConversation}
                disabled={conversationState === 'ending' || isRecording}
                variant="ghost"
                size="sm"
                className={cn(
                  'gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium h-auto shrink-0',
                  isRecording
                    ? 'bg-white/20 text-white/80 hover:bg-white/30'
                    : 'bg-slate-200/80 text-slate-600 hover:bg-slate-300/80'
                )}
              >
                <PhoneOff className="h-3.5 w-3.5" />
                <span>End</span>
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {conversationState === 'ending' ? 'Ending...' : 'End conversation'}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Large central push-to-talk button */}
        <div className="flex justify-center mb-3">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={onToggleRecording}
                onPointerDown={handlePointerDown}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                disabled={conversationState === 'ending'}
                size="xl"
                className={cn(
                  'relative rounded-full transition-all duration-200 active:scale-95 touch-none select-none',
                  isRecording
                    ? 'bg-white text-rose-600 shadow-2xl shadow-rose-900/30 hover:bg-white'
                    : 'bg-slate-800 text-white hover:bg-slate-700 shadow-xl shadow-slate-900/20 hover:shadow-2xl hover:shadow-slate-900/30'
                )}
              >
                {isRecording && (
                  <>
                    <span className="absolute inset-0 rounded-full bg-white/40 animate-ping" />
                    <span className="absolute -inset-3 rounded-full border-2 border-white/30 animate-pulse" />
                    <span className="absolute -inset-6 rounded-full border border-white/20 animate-pulse" style={{ animationDelay: '150ms' }} />
                  </>
                )}
                <Mic className={cn(
                  'relative z-10 transition-transform',
                  isRecording ? 'h-9 w-9 sm:h-10 sm:w-10' : 'h-8 w-8 sm:h-9 sm:w-9'
                )} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              {isRecording ? 'Release or tap to send' : 'Tap or hold to speak'}
            </TooltipContent>
          </Tooltip>
        </div>

        {/* Status text */}
        <div className={cn(
          'text-center h-5 transition-colors duration-200',
          isRecording ? 'text-white/90' : 'text-slate-500'
        )}>
          {isRecording ? (
            <span className="text-sm font-medium animate-pulse" role={showQuietHint ? 'status' : undefined}>
              {showQuietHint ? t('zine.speak.goAhead') : 'Recording... release or tap to send'}
            </span>
          ) : showQuietHint ? (
            <span className="text-sm font-semibold text-slate-800 animate-pulse" role="status">
              {t('zine.speak.goAhead')}
            </span>
          ) : currentTranscript ? (
            <span className="text-sm flex items-center justify-center gap-2">
              <span className="flex gap-1">
                <span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: '100ms' }} />
                <span className="h-1.5 w-1.5 rounded-full bg-rose-400 animate-bounce" style={{ animationDelay: '200ms' }} />
              </span>
              {currentTranscript}
            </span>
          ) : (
            <span className="text-sm">Tap or hold to speak</span>
          )}
        </div>
      </div>
    </div>
  );
}

interface ReadOnlyViewProps {
  isLoading: boolean;
  previousSessions: SessionSummary[];
  onViewSession: (session: SessionSummary) => void;
}

function ReadOnlyView({ isLoading, previousSessions, onViewSession }: ReadOnlyViewProps) {
  if (isLoading) {
    return (
      <div className="flex flex-col items-center space-y-4 py-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin text-rose-300" />
        <p className="text-sm text-gray-500">Loading conversation history...</p>
      </div>
    );
  }

  if (previousSessions.length === 0) {
    return (
      <div className="flex flex-col items-center space-y-4 py-8 text-center">
        <div className="flex h-16 w-16 items-center justify-center rounded-full bg-rose-100">
          <MessageCircle className="h-8 w-8 text-rose-300" />
        </div>
        <p className="text-sm text-gray-500">No conversation attempts yet.</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center space-y-4 py-8 text-center">
      <div className="w-full max-w-md space-y-3">
        <div className="flex items-center justify-center gap-2 text-sm text-gray-600">
          <History className="h-4 w-4" />
          <span>Conversation attempts ({previousSessions.length})</span>
        </div>
        <SessionHistoryList sessions={previousSessions} onSelect={onViewSession} />
      </div>
    </div>
  );
}
